<template>

    <!-- CHILDREN OR QUOTAS -->
    <div class="d-flex mt-1 mb-0" v-if="componentReady">

        <div class="align-self-center d-flex mr-2" v-if="mode==='create'">

            <!-- TOGGLE QUOTA ON / OFF ONLY IN EDIT MODE -->
            <v-btn :color="item.enabled ? 'green darken-2' : 'red darken-2'"
                   @click="enableButtonClicked(item)">

                <div v-if="item.enabled">
                    ON
                </div>

                <div v-else>
                    OFF
                </div>
            </v-btn>

        </div>

        <div class="d-flex" :style="!item.enabled ? 'opacity: 0.35;' : ''">

            <!-- QUOTA / OPTION NAME -->
            <div class="align-self-center">

                <v-card color="grey darken-1"
                        class="pa-2 pl-3 truncate"
                        height="36"
                        min-width="380"
                        max-width="380">
                    {{ item.name }}
                </v-card>

            </div>

            <!-- CURRENT COUNT -->
            <div class="align-self-center ml-2" >
                <v-card color="grey darken-1"
                        v-if="project.completed_count > 0"
                        class="d-flex"
                        height="36"
                        width="70">
                    <div class="align-self-center ml-3 body-1">
                        {{ item.count }}
                    </div>
                </v-card>
            </div>

            <!-- SLASH ICON -->
            <div class="align-self-center" v-if="project.completed_count > 0">
                <v-icon color="grey">mdi-slash-forward</v-icon>
            </div>

            <!-- QUOTA LIMIT -->
            <div class="align-self-center ml-0">

                <v-card v-if="mode==='runtime'" color="grey darken-1"
                        class="d-flex"
                        height="36"
                        width="70">
                    <div class="align-self-center ml-3 body-1">
                        <div v-if="item.enabled">
                            {{ item.quota }}
                        </div>
                    </div>
                </v-card>

                <!-- COUNT INPUT -->
                <v-text-field dense
                              v-if="mode==='create' && !percentMode"
                              v-model="item.quota"
                              background-color="grey darken-1"
                              :disabled="mode==='runtime' || !item.enabled"
                              solo
                              errors="errors"
                              class="elevation-0"
                              hide-details
                              style="width: 100px"
                              @input="itemQuotaModified(item)"
                ></v-text-field>

                <!-- PERCENT INPUT -->
                <v-text-field dense
                              v-if="mode==='create' && percentMode"
                              v-model="item.quota_percent"
                              background-color="grey darken-1"
                              :disabled="mode==='runtime' || !item.enabled"
                              solo
                              errors="errors"
                              class="elevation-0"
                              hide-details
                              style="width: 100px;"
                              suffix="%"
                              @input="itemQuotaPercentModified(item)"
                ></v-text-field>

            </div>

            <!-- PROGRESS BAR -->
            <quota-progress :mode="mode"
                            :item="item"
                            :show-analyze-results="showAnalyzeResults"
                            :feasibility-running="feasibilityRunning"
            ></quota-progress>

            <div v-if="showAnalyzeResults && item.availability_multiplier !==null" class="align-self-center ml-1">
                <v-icon color="grey">mdi-close</v-icon>
            </div>

            <!-- AVAILABILITY -->
            <quota-availability-box v-if="item.availability_multiplier !==null"
                                    :mode="mode"
                                    :item="item"
                                    :target-completes="project.target_completes"
                                    :show-analyze-results="showAnalyzeResults"
                                    :feasibility-running="feasibilityRunning"
            ></quota-availability-box>

            <!-- BOOST COLUMN -->
            <quota-boost-box
                v-if="mode==='runtime'"
                :item="item"
                :show-analyze-results="showAnalyzeResults"
                :feasibility-running="feasibilityRunning"
            ></quota-boost-box>

            <!-- QUOTA OVER SAMPLING TERMINATIONS -->
            <quota-over-sample-terminations-box v-if="mode === 'runtime'"
                                                :item="item"
                                                :target-completes="project.target_completes"
                                                :show-analyze-results="showAnalyzeResults"
                                                :feasibility-running="feasibilityRunning"
            ></quota-over-sample-terminations-box>

            <!-- RELAX TOGGLE -->
            <div :style="!item.enabled ? 'opacity: 0.35;' : ''">
                <v-tooltip top color="primary" open-delay="400">
                    <template v-slot:activator="{ on }">
                        <div v-on="on">
                            <v-switch v-model="item.enforce"
                                      class="align-self-center ml-3 mt-1"
                                      hide-details
                                      @change="enforceToggleOperated($event, item)"
                                      dense>
                            </v-switch>
                        </div>
                    </template>
                    Enforce quota
                </v-tooltip>
            </div>
        </div>

        <!-- WARNING ICON -->
        <div v-if="item.error" class="align-self-center d-flex">
            <v-icon color="yellow darken-1" class="mr-2">
                mdi-alert
            </v-icon>
            {{ item.error }}
        </div>

        <json v-if="false" class="caption" :data="item"/>
    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapState, mapActions} from 'vuex';
import QuotaAvailabilityBox from "./QuotaAvailabilityBox";
import QuotaOverSampleTerminationsBox from "./QuotaOverSampleTerminationsBox";
import QuotaBoostBox from "./QuotaBoostBox";
import QuotaProgress from "./QuotaProgress";

const vuexModule = 'ProjectManager';

export default {

    components: {
        QuotaProgress,
        QuotaBoostBox,
        QuotaAvailabilityBox,
        QuotaOverSampleTerminationsBox,
        Json
    },

    props: {
        itemData: {
            type: Object,
            required: true,
        },

        project: {
            type: Object,
            required: true,
        },

        mode: {
            type: String,
            required: true,
            validator: value => ['create', 'runtime'].includes(value)
        },

        feasibilityRunning: {
            type: Boolean,
            required: true,
            default: false,
        },

        showAnalyzeResults: {
            type: Boolean,
            required: true,
            default: false,
        },

        percentMode: {
            type: Boolean,
            required: true,
            default: false,
        }
    },

    computed: {
        ...mapState(vuexModule, []),

        componentReady() {
            return this.item !== null;
        }
    },

    mounted() {
        this.item = Object.assign({}, this.itemData);
    },

    watch: {
        itemData(data) {
            this.item = Object.assign({}, this.itemData, data);
        }
    },

    data() {
        return {
            item: null,
            elementColor: 'grey darken-3',
        }
    },

    methods: {
        ...mapActions(vuexModule, [
            'toggleEnforceQuota'
        ]),

        enableButtonClicked(item) {

            if (this.mode==='runtime') return;

            item.enabled = !item.enabled;

            this.$emit('change', item);
        },

        itemQuotaModified(item) {

            this.item.error = null;

            const quota = parseInt(item.quota);

            if (isNaN(quota) || !this.isInt(item.quota)) {
                this.item.error = "Invalid value";
            }

            if (quota < 0) this.item.error = "Quota must be 0 or higher";
            if (quota > 10000) this.item.error = "You are crazy! The max is 10000!"

            if (!this.item.error) {
                item.quota = quota;
                item.quota_percent = Math.round(quota / this.project.target_completes * 10000) / 100;
                this.$emit('change', item);
            }
        },

        itemQuotaPercentModified(item) {

            this.item.error = null;

            const quotaPercent = parseFloat(item.quota_percent);

            if (isNaN(quotaPercent)) this.item.error = "Invalid value";
            else if (quotaPercent < 0) this.item.error = "Quota must be 0% or higher";
            else if (quotaPercent > 100) this.item.error = "The max is 100%"

            if (!this.item.error) {

                item.quota_percent = quotaPercent;
                item.quota = Math.round(quotaPercent / 100 * this.project.target_completes);
                this.$emit('change', item);
            }
        },

        isInt(n) {
            return n % 1 === 0;
        },

          enforceToggleOperated(enforce, item) {

            if (this.mode === "runtime") {

                const data = {
                    enforce: enforce,
                    projectId: this.project.id,
                    quotaId: item.id,
                }

                this.toggleEnforceQuota(data)

                    .then(() => {

                        if (enforce) {
                            Bus.$emit('flash-message', {message: "Quota enforced!"});
                        } else {
                            Bus.$emit('flash-message', {message: "Quota overage allowed!"});
                        }

                        this.$emit('change', item);
                    })
                    .catch(error => {

                        if (error.response.state === 422) {
                            Bus.$emit('flash-message', {type: 'error', message: error.response.data.message});
                        } else {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'OOPS That failed! Please contact support if this issue persists!'
                            });
                        }
                    });
            }
        },

        emit() {
            this.$emit('change', this.item);
        }
    }
}
</script>
